import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  IconButton,
  Switch,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import * as Yup from "yup";
import { debounce } from "lodash";
// eslint-disable-next-line import/no-unresolved
import { Tooltip } from "@financeable-com-au/financeable-ui";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import { getShouldValidatePersonalFinanceItem } from "@/hooks/useRequiredFields/utils/requiredFieldValues";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import regex from "../../utils/regex";
import {
  ERRORS_MESSAGE,
  frequencyList,
  getIsConsumerOrPersonalApplication,
  liabilitiesTypesList,
  SHARE_OF_DISABLED_TOOLTIP,
  validFrequencyList,
  validLiabilityType,
} from "../../constants";
import { NumericFormatCustom, currencyMaskedValue } from "../../utils/currencyMaskFormat";
import {
  saveValue,
  createLiabilities,
  deleteLiabilities,
  updateLiabilities,
  userDetailsSelector,
  setRequiredFieldsErrors,
} from "../../store/slices/applicationFormSlice";
import { customerSelector } from "../../store/slices/customerSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import { PercentageFormatCustom } from "../../utils/percentageMaskFormat";
import { getErrorToastPayload } from "../../utils/commonFunctions";

export default function Liabilities({
  liabilities,
  customerId,
  applicationId,
  applicationType,
  personalFinLoaders,
  isDisableEdit,
  isDisabledSharesField,
}) {
  const dispatch = useDispatch();
  const { accessComponents } = useAccessAppComponent();
  const {
    application: {
      customer: { isLiabilitiesBufferEnabled, liabilitiesBuffer },
    },
  } = accessComponents;
  const { isCustomerLogin } = useSelector(customerSelector);
  const [liabilityList, setLiabilityList] = useState(liabilities || []);
  const { requireFieldErrors } = useSelector(userDetailsSelector);
  const isDisable = isCustomerLogin ? false : isDisableEdit;
  const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(applicationType);

  useEffect(() => {
    setLiabilityList(liabilities || []);
  }, [liabilities]);

  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName, doNotShowError }) =>
    getCustomerOrEntityLocalAndLenderError({ fieldError, localState: {}, fieldName, localFieldName, doNotShowError });
  // Lender errors only show on validated rows: the first row and any row holding data.
  const validatedRows = liabilityList.map((item, index) =>
    getShouldValidatePersonalFinanceItem({ listName: "liabilities", item, index }),
  );

  const [changeDetected, setChangeDetected] = useState(false);
  const isAddLiabilityLoader = personalFinLoaders?.addLiabilityLoader && personalFinLoaders?.id === customerId;

  const validationSchema = Yup.object({
    liabilitiesType: Yup.string()
      .oneOf(validLiabilityType, "Please select valid liability type")
      .required("Liability type is required"),
    outstandingAmount: Yup.string()
      .optional()
      .matches(regex.allowOnlyNumber, "Only numbers allowed.")
      .max(10, "Maximum of 7 digits"),
    limit: Yup.string()
      .optional()
      .matches(regex.allowOnlyNumber, "Only numbers allowed.")
      .max(10, "Maximum of 7 digits"),
    financier: Yup.string().required("Financier is required"),
    repaymentFrequency: Yup.string()
      .oneOf(validFrequencyList, "Please select valid frequency")
      .required("Frequency is required"),
    repaymentAmount: Yup.string()
      .optional()
      .matches(regex.allowOnlyNumber, "Only numbers allowed.")
      .max(10, "Maximum of 7 digits"),
    description: Yup.string().required("Description is required"),
    buffer: Yup.number().optional(),
    shareOfLiability: Yup.string()
      .matches(regex.allowOnlyNumber, "Only numbers allowed.")
      .matches(regex.percentageRegex, {
        message: "Percentage should be upto 100%.",
      })
      .max(100, "Maximum of 3 digits"),
  });

  const debouncedValidation = useCallback(
    debounce(async (fieldName, value) => {
      try {
        const field = fieldName.split("_")[0];
        await validationSchema.validateAt(field, { [field]: value });
        dispatch(
          setRequiredFieldsErrors({
            ...requireFieldErrors,
            [fieldName]: "",
          }),
        );
      } catch (error) {
        dispatch(
          setRequiredFieldsErrors({
            ...requireFieldErrors,
            [fieldName]: error.message,
          }),
        );
      }
    }, 300),
    [requireFieldErrors],
  );

  const onAddNewLiabilitiesItem = () => {
    const data = {
      liabilitiesType: "",
      outstandingAmount: "",
      limit: "",
      financier: "",
      repaymentFrequency: "",
      repaymentAmount: "",
      description: "",
      shareOfLiability: 100,
      buffer: 0,
      application: applicationId,
      customer: customerId,
    };

    dispatch(
      saveValue({
        personalFinLoaders: {
          ...personalFinLoaders,
          id: customerId,
        },
      }),
    );
    dispatch(createLiabilities({ data }));
  };

  const removeLiabilities = (Element) => {
    const data = {
      liabilityId: Element._id,
      application: applicationId,
      customer: Element.customer,
    };
    delete data._id;
    dispatch(
      saveValue({
        personalFinLoaders: {
          ...personalFinLoaders,
          id: Element._id,
        },
      }),
    );
    dispatch(deleteLiabilities({ data })).then((res) => {
      if (res?.error) {
        dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
      }
    });
  };

  const handle = {
    onBlur: (name, value, newID, index) => {
      const isError = getLocalAndLenderError({
        fieldError: requireFieldErrors[`${name}_${customerId}_${index}`],
        fieldName: `${name}_${customerId}_${index}`,
        localFieldName: name,
      });
      // Need to refactor error handling flow if we need to check error for other fields also.
      if (name === "shareOfLiability" && isError) return;

      const temp = [...liabilityList];

      temp.map((ele) => {
        if (ele._id === newID) {
          let updatedLiabilitiesData = {
            ...ele,
            [name]: value,
            liabilityId: newID,
            application: applicationId,
            customer: customerId,
          };
          delete updatedLiabilitiesData._id;
          if (changeDetected) {
            dispatch(updateLiabilities({ data: updatedLiabilitiesData }));
            setChangeDetected(false);
          }
          return updatedLiabilitiesData;
        } else {
          return ele;
        }
      });
    },
    onChange: async (field, value, newID) => {
      let name = field.split("_")[0];
      debouncedValidation(field, value);

      if (field.includes("liability_description")) {
        name = "description";
      }

      const temp = [...liabilityList];

      let isChangeDetected = false;

      const updateAssetType = temp.map((ele) => {
        if (!ele._id) {
          const dataWithOutId = {
            ...ele,
            [name]: value,
            application: applicationId,
            customer: customerId,
          };
          isChangeDetected = true;
          dispatch(createLiabilities({ data: dataWithOutId }));
        }
        if (ele._id === newID) {
          const updatedLiabilitiesData = {
            ...ele,
            [name]: value,
          };
          if (ele[name] !== value) {
            isChangeDetected = true;
          }
          return updatedLiabilitiesData;
        } else {
          return ele;
        }
      });
      setChangeDetected(isChangeDetected);
      if (isChangeDetected) {
        setLiabilityList(updateAssetType);
      }
    },
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }} style={{ marginBottom: "5px", marginTop: "20px" }}>
        Liabilities
      </Typography>
      <Grid container item spacing={3} style={{ marginBottom: "10px" }} alignItems="center">
        {liabilityList?.map((ele, index) => {
          const isCreditCard = ele?.liabilitiesType === "Credit card";
          const isBufferEnabled = isLiabilitiesBufferEnabled && isCreditCard;
          return (
            <Grid container item xs={12} spacing={1} key={ele?._id}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled">
                  <InputLabel
                    id={`liability-type-label-${ele?._id}`}
                    error={getLocalAndLenderError({
                      fieldName: `liabilities.[0].liabilitiesType_${customerId}_${index}`,
                      doNotShowError: !!ele?.liabilitiesType || !validatedRows[index],
                    })}
                  >
                    Liabilities Type
                  </InputLabel>
                  <Select
                    size="small"
                    labelId={`liability-type-label-${ele?._id}`}
                    id={`liability-type-${ele?._id}`}
                    disabled={isDisable}
                    value={ele?.liabilitiesType}
                    label="Liabilities Type"
                    name={`liabilitiesType_${customerId}_${index}`}
                    onChange={(event) =>
                      handle.onChange(`liabilitiesType_${customerId}_${index}`, event.target.value, ele?._id)
                    }
                    onBlur={() => handle.onBlur("liabilitiesType", ele?.liabilitiesType, ele?._id, index)}
                    error={getLocalAndLenderError({
                      fieldName: `liabilities.[0].liabilitiesType_${customerId}_${index}`,
                      doNotShowError: !!ele?.liabilitiesType || !validatedRows[index],
                    })}
                  >
                    {liabilitiesTypesList.map((i) => (
                      <MenuItem value={i.value}>{i.label}</MenuItem>
                    ))}
                  </Select>
                  {getLocalAndLenderError({
                    fieldName: `liabilities.[0].liabilitiesType_${customerId}_${index}`,
                    doNotShowError: !!ele?.liabilitiesType || !validatedRows[index],
                  }) && (
                    <FormHelperText sx={{ color: "#d32f2f" }}>
                      {getLocalAndLenderError({
                        fieldName: `liabilities.[0].liabilitiesType_${customerId}_${index}`,
                        doNotShowError: !!ele?.liabilitiesType || !validatedRows[index],
                      })}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`liability-outstanding-amount-${ele?._id}`}
                  fullWidth
                  type="text"
                  inputProps={{ min: 0 }}
                  label="Outstanding Amount"
                  variant="filled"
                  value={ele?.outstandingAmount}
                  disabled={isDisable}
                  name={`outstandingAmount_${customerId}_${index}`}
                  error={getLocalAndLenderError({
                    fieldName: `liabilities.[0].outstandingAmount_${customerId}_${index}`,
                    doNotShowError: !!ele?.outstandingAmount || !validatedRows[index],
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `liabilities.[0].outstandingAmount_${customerId}_${index}`,
                    doNotShowError: !!ele?.outstandingAmount || !validatedRows[index],
                  })}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                  }}
                  onChange={(event) =>
                    handle.onChange(
                      `outstandingAmount_${customerId}_${index}`,
                      currencyMaskedValue(event.target.value),
                      ele?._id,
                    )
                  }
                  onBlur={() => handle.onBlur("outstandingAmount", ele?.outstandingAmount, ele?._id, index)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`liability-limit-${ele?._id}`}
                  type="text"
                  inputProps={{ min: 0 }}
                  label="Limit"
                  variant="filled"
                  style={{ width: "100%" }}
                  value={ele?.limit}
                  disabled={isDisable}
                  name={`limit_${customerId}_${index}`}
                  error={getLocalAndLenderError({
                    fieldName: `liabilities.[0].limit_${customerId}_${index}`,
                    doNotShowError: !!ele?.limit || !validatedRows[index],
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `liabilities.[0].limit_${customerId}_${index}`,
                    doNotShowError: !!ele?.limit || !validatedRows[index],
                  })}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                  }}
                  onChange={(event) => handle.onChange(`limit_${customerId}_${index}`, event.target.value, ele?._id)}
                  onBlur={() => handle.onBlur("limit", currencyMaskedValue(ele?.limit), ele?._id, index)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`liability-financier-${ele?._id}`}
                  type="text"
                  label="Financier"
                  variant="filled"
                  style={{ width: "100%" }}
                  value={ele?.financier}
                  disabled={isDisable}
                  name={`financier_${customerId}_${index}`}
                  error={getLocalAndLenderError({
                    fieldName: `liabilities.[0].financier_${customerId}_${index}`,
                    doNotShowError: !!ele?.financier || !validatedRows[index],
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `liabilities.[0].financier_${customerId}_${index}`,
                    doNotShowError: !!ele?.financier || !validatedRows[index],
                  })}
                  onChange={(event) =>
                    handle.onChange(`financier_${customerId}_${index}`, event.target.value, ele?._id)
                  }
                  onBlur={() => handle.onBlur("financier", ele?.financier, ele?._id, index)}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled">
                  <InputLabel
                    id={`liability-repayment-frequency-label-${ele?._id}`}
                    error={getLocalAndLenderError({
                      fieldName: `liabilities.[0].repaymentFrequency_${customerId}_${index}`,
                      doNotShowError: !!ele?.repaymentFrequency || !validatedRows[index],
                    })}
                  >
                    Repayment Frequency
                  </InputLabel>
                  <Select
                    size="small"
                    labelId={`liability-repayment-frequency-label-${ele?._id}`}
                    id={`liability-repayment-frequency-${ele?._id}`}
                    value={ele?.repaymentFrequency}
                    disabled={isDisable}
                    label="Repayment Frequency"
                    name={`repaymentFrequency_${customerId}_${index}`}
                    onChange={(event) =>
                      handle.onChange(`repaymentFrequency_${customerId}_${index}`, event.target.value, ele?._id)
                    }
                    onBlur={() => handle.onBlur("repaymentFrequency", ele?.repaymentFrequency, ele?._id, index)}
                    error={getLocalAndLenderError({
                      fieldName: `liabilities.[0].repaymentFrequency_${customerId}_${index}`,
                      doNotShowError: !!ele?.repaymentFrequency || !validatedRows[index],
                    })}
                  >
                    {frequencyList.map((i) => (
                      <MenuItem value={i.value}>{i.label}</MenuItem>
                    ))}
                  </Select>
                  {getLocalAndLenderError({
                    fieldName: `liabilities.[0].repaymentFrequency_${customerId}_${index}`,
                    doNotShowError: !!ele?.repaymentFrequency || !validatedRows[index],
                  }) && (
                    <FormHelperText sx={{ color: "#d32f2f" }}>
                      {getLocalAndLenderError({
                        fieldName: `liabilities.[0].repaymentFrequency_${customerId}_${index}`,
                        doNotShowError: !!ele?.repaymentFrequency || !validatedRows[index],
                      })}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  id={`liability-repayment-amount-${ele?._id}`}
                  type="text"
                  inputProps={{ min: 0 }}
                  label="Repayment amount"
                  variant="filled"
                  size="small"
                  style={{ width: "100%" }}
                  value={ele?.repaymentAmount}
                  disabled={isDisable}
                  name={`repaymentAmount_${customerId}_${index}`}
                  error={getLocalAndLenderError({
                    fieldName: `liabilities.[0].repaymentAmount_${customerId}_${index}`,
                    doNotShowError: !!ele?.repaymentAmount || !validatedRows[index],
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `liabilities.[0].repaymentAmount_${customerId}_${index}`,
                    doNotShowError: !!ele?.repaymentAmount || !validatedRows[index],
                  })}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                  }}
                  onChange={(event) =>
                    handle.onChange(
                      `repaymentAmount_${customerId}_${index}`,
                      currencyMaskedValue(event.target.value),
                      ele?._id,
                    )
                  }
                  onBlur={() => handle.onBlur("repaymentAmount", ele?.repaymentAmount, ele?._id, index)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  id={`liability-description-${ele?._id}`}
                  type="text"
                  label="Description"
                  variant="filled"
                  style={{ width: "100%", flex: "1" }}
                  value={ele?.description}
                  disabled={isDisable}
                  name={`liability_description_${customerId}_${index}`}
                  error={getLocalAndLenderError({
                    fieldName: `liabilities.[0].description_${customerId}_${index}`,
                    doNotShowError: !!ele?.description || !validatedRows[index],
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `liabilities.[0].description_${customerId}_${index}`,
                    doNotShowError: !!ele?.description || !validatedRows[index],
                  })}
                  onChange={(event) =>
                    handle.onChange(`liability_description_${customerId}_${index}`, event.target.value, ele?._id)
                  }
                  onBlur={() => handle.onBlur("description", ele?.description, ele?._id, index)}
                />
              </Grid>
              {isConsumerOrPersonalApplication && (
                <Grid item xs={12} sm={2.5}>
                  <Tooltip
                    title={isDisabledSharesField ? SHARE_OF_DISABLED_TOOLTIP : ""}
                    placement="top"
                    size="sm"
                    disableHoverListener={!isDisabledSharesField}
                    disableFocusListener={!isDisabledSharesField}
                    disableTouchListener={!isDisabledSharesField}
                  >
                    <span style={{ display: "block", width: "100%" }}>
                      <TextField
                        size="small"
                        id={`liability-shareOfLiability-${ele?._id}`}
                        type="text"
                        inputProps={{ min: 0 }}
                        label="Share of"
                        variant="filled"
                        style={{ width: "100%" }}
                        value={ele?.shareOfLiability}
                        disabled={isDisabledSharesField}
                        name={`shareOfLiability_${customerId}_${index}`}
                        error={getLocalAndLenderError({
                          fieldError: requireFieldErrors[`shareOfLiability_${customerId}_${index}`],
                          fieldName: `shareOfLiability_${customerId}_${index}`,
                          localFieldName: "shareOfLiability",
                        })}
                        helperText={getLocalAndLenderError({
                          fieldError: requireFieldErrors[`shareOfLiability_${customerId}_${index}`],
                          fieldName: `shareOfLiability_${customerId}_${index}`,
                          localFieldName: "shareOfLiability",
                        })}
                        InputProps={{
                          inputComponent: PercentageFormatCustom,
                        }}
                        onChange={(event) =>
                          handle.onChange(`shareOfLiability_${customerId}_${index}`, event.target.value, ele?._id)
                        }
                        onBlur={() => handle.onBlur("shareOfLiability", ele?.shareOfLiability || 0, ele?._id, index)}
                      />
                    </span>
                  </Tooltip>
                </Grid>
              )}
              {isBufferEnabled ? (
                <Grid item xs={12} sm={2} sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={typeof ele?.buffer === "number" ? Number(ele.buffer) > 0 : Boolean(ele?.buffer)}
                        onChange={(event) => {
                          const value = event.target.checked ? liabilitiesBuffer : 0;
                          handle.onChange(`buffer_${customerId}_${index}`, value, ele?._id, index);
                        }}
                        onBlur={() => handle.onBlur("buffer", Number(ele?.buffer) || 0, ele?._id)}
                        disabled={isDisable}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Buffer (3.8%)"
                    labelPlacement="right"
                    sx={{ ml: 0, "& .MuiFormControlLabel-label": { fontSize: "0.82rem" } }}
                  />
                </Grid>
              ) : (
                <></>
              )}

              <Grid item xs={12} sm={0.5}>
                <IconButton
                  size="small"
                  aria-label={`Remove liability ${index + 1}`}
                  title={
                    personalFinLoaders?.removeLiabilityLoader && personalFinLoaders?.id === ele?._id
                      ? "Removing..."
                      : "Remove liability"
                  }
                  sx={{ marginTop: "10px", color: "error.main" }}
                  onClick={() => removeLiabilities(ele)}
                  disabled={isDisable || personalFinLoaders?.removeLiabilityLoader}
                >
                  <DeleteOutlineIcon fontSize="medium" />
                </IconButton>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      <Grid item xs={12}>
        <div>
          {/* Allow for adding first liability when the liability is an empty list or is undefined. */}
          <Button
            onClick={() => onAddNewLiabilitiesItem()}
            variant="outlined"
            size="small"
            disabled={
              isAddLiabilityLoader ||
              isDisable ||
              (liabilityList?.length > 0 &&
                !liabilityList?.[0]?.liabilitiesType &&
                !liabilityList?.[0]?.outstandingAmount &&
                !liabilityList?.[0]?.limit &&
                !liabilityList?.[0]?.financier &&
                !liabilityList?.[0]?.repaymentFrequency &&
                !liabilityList?.[0]?.repaymentAmount &&
                !liabilityList?.[0]?.description)
            }
          >
            {isAddLiabilityLoader ? "Adding..." : "Add liability"}
          </Button>
        </div>
      </Grid>
    </>
  );
}
